import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Video, Play, Loader2, CheckCircle, XCircle, RefreshCw, 
  Zap, Send, Clock, Film, ChevronDown, ChevronUp
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  processing: 'text-yellow-400',
  completed: 'text-green-400',
  failed: 'text-red-400',
  pending: 'text-gray-400'
};

const STATUS_ICONS = {
  processing: Loader2,
  completed: CheckCircle,
  failed: XCircle,
  pending: Clock
};

const HeyGenAdmin = () => {
  const [scripts, setScripts] = useState([]);
  const [videos, setVideos] = useState({});
  const [generating, setGenerating] = useState({});
  const [polling, setPolling] = useState({});
  const [batchMode, setBatchMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [voiceId, setVoiceId] = useState('8faa09e16e7e48f59d6cc8d7e3c537fc');
  const [expandedScript, setExpandedScript] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [scriptsRes, videosRes] = await Promise.all([
        api.get('/heygen/scripts'),
        api.get('/heygen/videos')
      ]);
      setScripts(scriptsRes.data.scripts || []);
      const videoMap = {};
      (videosRes.data.videos || []).forEach(v => {
        if (!videoMap[v.script_key] || v.created_at > videoMap[v.script_key].created_at) {
          videoMap[v.script_key] = v;
        }
      });
      setVideos(videoMap);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const generateSingle = async (scriptKey) => {
    setGenerating(prev => ({ ...prev, [scriptKey]: true }));
    try {
      const res = await api.post('/heygen/generate', { script_key: scriptKey, voice_id: voiceId });
      const videoId = res.data.video?.video_id;
      toast.success(`Video generation started for ${scriptKey}`);
      if (videoId) pollStatus(scriptKey, videoId);
    } catch (err) {
      toast.error(`Failed to generate: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGenerating(prev => ({ ...prev, [scriptKey]: false }));
    }
  };

  const generateBatch = async () => {
    if (selectedKeys.length === 0) {
      toast.error('Select at least one script');
      return;
    }
    setGenerating(prev => {
      const newState = { ...prev };
      selectedKeys.forEach(k => { newState[k] = true; });
      return newState;
    });
    try {
      const res = await api.post('/heygen/generate-batch', { script_keys: selectedKeys, voice_id: voiceId });
      toast.success(`${res.data.total_submitted} videos submitted for generation`);
      (res.data.videos || []).forEach(v => {
        if (v.video_id) pollStatus(v.key, v.video_id);
      });
      setSelectedKeys([]);
      setBatchMode(false);
    } catch (err) {
      toast.error(`Batch generation failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGenerating(prev => {
        const newState = { ...prev };
        selectedKeys.forEach(k => { newState[k] = false; });
        return newState;
      });
    }
  };

  const pollStatus = (scriptKey, videoId) => {
    setPolling(prev => ({ ...prev, [scriptKey]: true }));
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/heygen/status/${videoId}`);
        const status = res.data.video?.status;
        if (status === 'completed') {
          clearInterval(interval);
          setPolling(prev => ({ ...prev, [scriptKey]: false }));
          setVideos(prev => ({ ...prev, [scriptKey]: { ...res.data.video, script_key: scriptKey } }));
          toast.success(`Video ready: ${scriptKey}`);
          // Auto-apply to curriculum
          try { await api.post(`/heygen/apply-video/${scriptKey}`); } catch {}
        } else if (status === 'failed') {
          clearInterval(interval);
          setPolling(prev => ({ ...prev, [scriptKey]: false }));
          toast.error(`Video failed: ${scriptKey}`);
        }
      } catch {
        clearInterval(interval);
        setPolling(prev => ({ ...prev, [scriptKey]: false }));
      }
    }, 15000); // Poll every 15 seconds
  };

  const checkStatus = async (scriptKey) => {
    const video = videos[scriptKey];
    if (!video?.video_id) return;
    try {
      const res = await api.get(`/heygen/status/${video.video_id}`);
      setVideos(prev => ({ ...prev, [scriptKey]: { ...res.data.video, script_key: scriptKey } }));
      if (res.data.video?.status === 'completed') {
        toast.success('Video is ready!');
        try { await api.post(`/heygen/apply-video/${scriptKey}`); } catch {}
      } else {
        toast.info(`Status: ${res.data.video?.status}`);
      }
    } catch (err) {
      toast.error('Failed to check status');
    }
  };

  const toggleSelect = (key) => {
    setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => {
    const ungenerated = scripts.filter(s => !videos[s.key]?.video_url).map(s => s.key);
    setSelectedKeys(ungenerated);
  };

  const getScriptGroup = (key) => {
    if (key.startsWith('orientation_')) return 'Orientation';
    if (key.startsWith('week1_')) return 'Week 1';
    if (key.startsWith('week2_')) return 'Week 2';
    if (key.startsWith('week3_')) return 'Week 3';
    if (key.startsWith('week4_')) return 'Week 4';
    if (key.startsWith('coach_')) return 'Coach\'s Corner';
    return 'Other';
  };

  const grouped = scripts.reduce((acc, s) => {
    const group = getScriptGroup(s.key);
    if (!acc[group]) acc[group] = [];
    acc[group].push(s);
    return acc;
  }, {});

  const completedCount = scripts.filter(s => videos[s.key]?.status === 'completed' || videos[s.key]?.video_url).length;
  const processingCount = scripts.filter(s => videos[s.key]?.status === 'processing' || polling[s.key]).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-16" data-testid="heygen-admin">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Film className="w-8 h-8 text-orange-500" />
            <h1 className="text-3xl font-black text-white" data-testid="heygen-admin-title">HeyGen Video Manager</h1>
          </div>
          <p className="text-gray-400">Generate AI avatar videos for Academy courses. Videos auto-replace YouTube placeholders.</p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{scripts.length}</div>
              <div className="text-xs text-gray-500">Total Scripts</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{completedCount}</div>
              <div className="text-xs text-gray-500">Videos Ready</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400">{processingCount}</div>
              <div className="text-xs text-gray-500">Processing</div>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Button
            onClick={() => setBatchMode(!batchMode)}
            variant={batchMode ? 'default' : 'outline'}
            className={batchMode ? 'bg-orange-600 hover:bg-orange-700' : 'border-gray-700 text-gray-300'}
            data-testid="batch-mode-toggle"
          >
            <Zap className="w-4 h-4 mr-2" />
            {batchMode ? 'Exit Batch Mode' : 'Batch Generate'}
          </Button>

          {batchMode && (
            <>
              <Button onClick={selectAll} variant="outline" className="border-gray-700 text-gray-300" data-testid="select-all-btn">
                Select All Ungenerated
              </Button>
              <Button
                onClick={generateBatch}
                className="bg-green-600 hover:bg-green-700"
                disabled={selectedKeys.length === 0}
                data-testid="generate-batch-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Generate {selectedKeys.length} Videos
              </Button>
            </>
          )}

          <Button onClick={loadData} variant="outline" className="border-gray-700 text-gray-300 ml-auto" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Script Groups */}
        {Object.entries(grouped).map(([group, groupScripts]) => (
          <div key={group} className="mb-8">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Video className="w-5 h-5 text-orange-500" />
              {group}
              <span className="text-sm font-normal text-gray-500">
                ({groupScripts.filter(s => videos[s.key]?.video_url).length}/{groupScripts.length} ready)
              </span>
            </h2>

            <div className="space-y-2">
              {groupScripts.map(script => {
                const video = videos[script.key];
                const isComplete = video?.status === 'completed' || video?.video_url;
                const isProcessing = video?.status === 'processing' || polling[script.key];
                const isGenerating = generating[script.key];
                const StatusIcon = STATUS_ICONS[isComplete ? 'completed' : isProcessing ? 'processing' : 'pending'];

                return (
                  <Card key={script.key} className={`bg-gray-900 border ${isComplete ? 'border-green-800/50' : 'border-gray-800'}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        {batchMode && !isComplete && (
                          <input
                            type="checkbox"
                            checked={selectedKeys.includes(script.key)}
                            onChange={() => toggleSelect(script.key)}
                            className="w-4 h-4 accent-orange-500"
                            data-testid={`checkbox-${script.key}`}
                          />
                        )}
                        
                        <StatusIcon className={`w-5 h-5 flex-shrink-0 ${STATUS_COLORS[isComplete ? 'completed' : isProcessing ? 'processing' : 'pending']} ${isProcessing ? 'animate-spin' : ''}`} />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">{script.title}</span>
                            <span className="text-gray-600 text-xs font-mono">{script.key}</span>
                          </div>
                          {isComplete && video?.video_url && (
                            <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:underline truncate block">
                              {video.video_url.substring(0, 60)}...
                            </a>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setExpandedScript(expandedScript === script.key ? null : script.key)}
                            className="text-gray-500 hover:text-white p-1"
                          >
                            {expandedScript === script.key ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {isComplete ? (
                            <Button size="sm" variant="outline" onClick={() => checkStatus(script.key)} className="border-green-800 text-green-400 text-xs" data-testid={`status-${script.key}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Ready
                            </Button>
                          ) : isProcessing ? (
                            <Button size="sm" variant="outline" onClick={() => checkStatus(script.key)} className="border-yellow-800 text-yellow-400 text-xs" data-testid={`status-${script.key}`}>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => generateSingle(script.key)}
                              disabled={isGenerating}
                              className="bg-orange-600 hover:bg-orange-700 text-xs"
                              data-testid={`generate-${script.key}`}
                            >
                              {isGenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                              Generate
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Expanded script preview */}
                      {expandedScript === script.key && (
                        <div className="mt-3 p-3 bg-gray-800 rounded text-gray-300 text-sm leading-relaxed">
                          {CURRICULUM_SCRIPTS_FULL[script.key] || script.script_preview}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// We'll load full scripts from the API for preview; this is just a fallback
const CURRICULUM_SCRIPTS_FULL = {};

export default HeyGenAdmin;
